import React from 'react';
import { IHTCalculation, EstateAssets, EstateProfile } from '../../types';
import { hex } from '../designTokens';

type ConfidenceLevel = 'high' | 'medium' | 'low';

type ConfidenceFactor = {
    label: string;
    met: boolean;
};

function computeConfidence(
    ihtCalc: IHTCalculation | null | undefined,
    assets: EstateAssets | undefined,
    profile: EstateProfile | undefined,
): { level: ConfidenceLevel; factors: ConfidenceFactor[]; reason: string | null } {
    const factors: ConfidenceFactor[] = [];
    let score = 100;

    const b = ihtCalc?.breakdown;
    const sp = profile?.iht_details?.predeceased_spouse;
    const ts = profile?.iht_details?.trust_screening;

    // --- Data completeness checks ---
    const propsWithValues = (assets?.properties ?? []).filter(p => p.estimated_value && p.estimated_value > 0);
    const propsTotal = (assets?.properties ?? []).length;
    const propertyValuesComplete = propsTotal === 0 || propsWithValues.length === propsTotal;
    factors.push({ label: 'Property values provided', met: propertyValuesComplete });
    if (!propertyValuesComplete) score -= 15;

    const banksWithValues = (assets?.bank_accounts ?? []).filter(a => a.estimated_value && a.estimated_value > 0);
    const invsWithValues = (assets?.investments ?? []).filter(i => i.estimated_value && i.estimated_value > 0);
    const assetValuesProvided = (banksWithValues.length + invsWithValues.length) > 0 ||
        ((assets?.bank_accounts ?? []).length === 0 && (assets?.investments ?? []).length === 0);
    factors.push({ label: 'Asset values provided', met: assetValuesProvided });
    if (!assetValuesProvided) score -= 10;

    const giftsConfirmed = (assets?.lifetime_gifts ?? []).length > 0 ||
        assets?.gifts_last_7_years?.exists === false ||
        assets?.gifts_last_7_years?.exists === true;
    factors.push({ label: 'Gifts confirmed', met: giftsConfirmed });
    if (!giftsConfirmed) score -= 10;

    const trustAnswered = ts?.created_or_transferred_trust !== undefined && ts?.created_or_transferred_trust !== null ||
        ts?.has_trusts !== undefined && ts?.has_trusts !== null;
    factors.push({ label: 'Trust answers provided', met: !!trustAnswered });
    if (!trustAnswered) score -= 10;

    const unknownOwnership = [
        ...(assets?.properties ?? []).filter(p => !p.ownership_type || p.ownership_type === 'unknown'),
        ...(assets?.bank_accounts ?? []).filter(a => !a.ownership),
        ...(assets?.investments ?? []).filter(i => !i.ownership),
    ];
    factors.push({ label: 'Ownership information known', met: unknownOwnership.length === 0 });
    if (unknownOwnership.length > 0) score -= Math.min(15, unknownOwnership.length * 5);

    // --- Negative factors (complexity) ---
    if (ts?.trust_involvement_detected === true) score -= 25;
    else if (ts?.trust_involvement_uncertain === true) score -= 15;
    if (ts?.gwr_likely === true) score -= 25;
    else if (ts?.gwr_possible === true) score -= 15;
    if (ts?.has_loan_trust_or_discounted_gift === true) score -= 30;

    if (sp?.exists === true) {
        if (sp.confidence === 'possible' || !sp.unused_nrb_percent) score -= 10;
        if (sp.records_available === 'none') score -= 10;
    }

    if (ihtCalc?.blockers && ihtCalc.blockers.length > 0) score -= Math.min(20, ihtCalc.blockers.length * 5);

    if (assets?.foreign_assets?.exists === true) {
        score -= assets.foreign_assets.exact_value_entered ? 5 : 10;
    }

    if (assets?.life_insurance?.exists === true && assets.life_insurance.written_in_trust === null) score -= 10;

    if (!((b?.gross_value ?? 0) > 0 && (b?.net_value ?? 0) > 0)) score -= 15;

    const clampedScore = Math.max(0, Math.min(100, score));
    const scored: ConfidenceLevel = clampedScore >= 75 ? 'high' : clampedScore >= 45 ? 'medium' : 'low';

    // WP1: the engine reports trust property that may belong in the estate but is not included, so the result is
    // incomplete. That is an unmet factor and caps the level at Low, whatever the score; nothing else changes.
    if (b?.trust_estate_inclusion_gap) {
        factors.push({ label: 'Trust property included in the estate', met: false });
        return { level: 'low', factors, reason: TRUST_ESTATE_REASON };
    }
    return { level: scored, factors, reason: null };
}

const TRUST_ESTATE_REASON =
    'Trust property that may belong in the estate has not been included, so the calculation is incomplete and the figures are provisional. A trust or inheritance tax specialist should review the trust before you rely on them.';

const LEVEL_CONFIG: Record<ConfidenceLevel, {
    label: string; description: string;
}> = {
    high: {
        label: 'High',
        description: 'This estimate is based on key estate information that has been provided and is likely to be accurate.',
    },
    medium: {
        label: 'Moderate',
        description: 'Some details are estimated or unconfirmed. The figure may change as more information is provided.',
    },
    low: {
        label: 'Low',
        description: 'Significant information is missing or uncertain. Treat this estimate with caution.',
    },
};

interface Props {
    ihtCalc?: IHTCalculation | null;
    assets?: EstateAssets;
    profile?: EstateProfile;
}

export const EstimateConfidence: React.FC<Props> = ({ ihtCalc, assets, profile }) => {
    const { level, factors, reason } = computeConfidence(ihtCalc, assets, profile);
    const cfg = LEVEL_CONFIG[level];
    const metCount = factors.filter(f => f.met).length;
    const totalCount = factors.length;
    const percent = Math.round((metCount / Math.max(1, totalCount)) * 100);

    return (
        <div
            className="rounded p-5"
            style={{ background: hex.surface, border: `1px solid ${hex.border}` }}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-[18px] font-medium text-[#1A1A1A] m-0">Estimate reliability</h3>
                <span className="font-serif italic text-[15px] text-[#1A1A1A]">
                    {cfg.label}
                </span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-[#E8E4DB] rounded-full h-1.5 mb-4">
                <div
                    className="h-1.5 rounded-full bg-[#3730A3] transition-all"
                    style={{ width: `${percent}%` }}
                />
            </div>

            {/* Description */}
            <p className="text-[13px] text-[#3F3D39] leading-relaxed mb-4">
                {cfg.description}
            </p>
            {reason && (
                <p data-testid="estimate-confidence-reason" className="text-[13px] text-[#1A1A1A] leading-relaxed mb-4">
                    {reason}
                </p>
            )}

            {/* Checklist */}
            <div className="space-y-1.5">
                {factors.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-[13px]">
                        {f.met
                            ? <span className="font-serif text-[#3730A3] shrink-0 w-3.5 text-center">✓</span>
                            : <span className="font-serif text-[#AFA99F] shrink-0 w-3.5 text-center">·</span>}
                        <span className={f.met ? 'text-[#3F3D39]' : 'text-[#AFA99F]'}>{f.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
